/**
 * Safely parse JSON strings with a fallback value.
 * This is a best practice for handling potentially malformed JSON from backend responses.
 *
 * @param value - The value to parse (can be string, object, or undefined)
 * @param fallback - The fallback value to return if parsing fails
 * @param logContext - Optional context string for logging warnings
 * @returns The parsed value or fallback
 */
export function safeJsonParse<T>(
  value: string | T | undefined | null,
  fallback: T,
  logContext?: string,
): T {
  if (value === undefined || value === null) {
    return fallback;
  }

  // If it's already an object, return it
  if (typeof value !== 'string') {
    return value;
  }

  // Handle edge case of literal "undefined" string
  if (value === 'undefined') {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch (e) {
    if (logContext) {
      console.warn(`[${logContext}] Failed to parse JSON:`, e);
    }
    return fallback;
  }
}
